import { useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Paperclip, X, FileIcon, ImageIcon, FileText, File } from "lucide-react";

function getFileIcon(type) {
  if (type.startsWith("image/")) return ImageIcon;
  if (type.includes("pdf")) return FileText;
  if (type.includes("text")) return FileText;
  return File;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function Attachments({ attachments, onAttachmentsChange }) {
  const inputRef = useRef(null);

  const handleFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    const readers = fileArray.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target.result.split(",")[1];
          resolve({
            id: `${file.name}-${Date.now()}-${Math.random()}`,
            name: file.name,
            size: file.size,
            content_type: file.type || "application/octet-stream",
            data: base64,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then((newAttachments) => {
      onAttachmentsChange((prev) => [...prev, ...newAttachments]);
    });
  }, [onAttachmentsChange]);

  const handleChange = useCallback((e) => {
    if (e.target.files.length) {
      handleFiles(e.target.files);
      e.target.value = "";
    }
  }, [handleFiles]);

  const handleRemove = useCallback((id) => {
    onAttachmentsChange((prev) => prev.filter((a) => a.id !== id));
  }, [onAttachmentsChange]);

  const totalSize = attachments.reduce((sum, a) => sum + a.size, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-3"
      data-testid="attachments-section"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-bold tracking-wide uppercase text-zinc-500">
            Pièces jointes
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {attachments.length > 0 && (
            <Badge variant="outline" className="text-xs font-mono" data-testid="attachments-count-badge">
              {attachments.length} fichier{attachments.length > 1 ? "s" : ""} — {formatSize(totalSize)}
            </Badge>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleChange}
        data-testid="attachments-file-input"
      />

      <Button
        variant="outline"
        className="gap-2 w-full border-dashed border-2 h-11 text-zinc-500 hover:text-primary hover:border-primary/50 transition-all"
        onClick={() => inputRef.current?.click()}
        data-testid="add-attachment-button"
      >
        <Paperclip className="w-4 h-4" />
        Ajouter des pièces jointes
      </Button>

      <AnimatePresence>
        {attachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2"
          >
            {attachments.map((att) => {
              const IconComp = getFileIcon(att.content_type);
              return (
                <motion.div
                  key={att.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  className="flex items-center gap-3 p-3 rounded-lg border border-zinc-100 bg-zinc-50/50 group"
                  data-testid={`attachment-item-${att.name}`}
                >
                  <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <IconComp className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-700 truncate">{att.name}</p>
                    <p className="text-xs text-zinc-400">{formatSize(att.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-zinc-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemove(att.id)}
                    data-testid={`remove-attachment-${att.name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {totalSize > 10 * 1024 * 1024 && (
        <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="attachment-size-warning">
          Attention : la taille totale dépasse 10 Mo. Certains serveurs SMTP peuvent rejeter l'envoi.
        </p>
      )}
    </motion.div>
  );
}
